/**
 * NodeInspectBadge -- Clickable badge on each node after test run
 *
 * Shows green checkmark (success), red X (error), or amber warning (partial).
 * Click opens a popover with input data, output data, duration, and
 * a toggle between Pretty (formatted JSON) and Raw views.
 *
 * Appears with a pop animation (scale 0->1, 200ms) staggered per node.
 */

import { useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

// ── Types ───────────────────────────────────────────────────────────────

export interface NodeInspectData {
  status: 'success' | 'error' | 'partial';
  durationMs: number;
  input: Record<string, unknown>;
  /**
   * #197 / #205 — `null` is the truth-telling shape for a step that
   * was persisted with no output (e.g. a delay node, a stop node, or
   * any kind whose executor returned `null`). The badge renders this
   * as a clear "(no output)" placeholder instead of fabricating a
   * synthetic success blob.
   */
  output: Record<string, unknown> | null;
}

interface NodeInspectBadgeProps {
  data: NodeInspectData;
  /** Stagger index for the pop-in animation (multiplied by 80ms delay) */
  staggerIndex?: number;
}

// ── Synthetic preview generator (#F9) ───────────────────────────────────
//
// Earlier this file shipped a hardcoded `MOCK_INPUTS` + `MOCK_OUTPUTS`
// catalogue with fabricated production-looking data ("assetId: P-401",
// "alarmId: ALM-2847", "model: qwen-72b-awq", "Sodium Hydroxide SDS"
// recommendations) that would render verbatim regardless of the
// workflow being tested. Founder rule: production must not show
// fabricated data.
//
// `generateSyntheticInspectData` now produces a config-derived,
// clearly-labelled placeholder. The badge renders a "Test data —
// synthetic preview" banner so reviewers can never confuse it with a
// real execution payload. The real test-run engine will replace this
// with actual per-node output once that wiring lands.

export function generateSyntheticInspectData(
  nodeType: string,
  config: Record<string, unknown> | undefined,
  status: 'success' | 'error' | 'partial',
): NodeInspectData {
  const cfg = config ?? {};
  const input: Record<string, unknown> = {
    _synthetic: true,
    nodeType,
    note: 'No real input captured — placeholder generated from this node\'s configuration.',
  };
  // Surface the fields the user actually configured, so the preview is
  // tied to their workflow, not a fictional pump scenario.
  if (cfg.triggerType) input.triggerType = cfg.triggerType;
  if (cfg.eventType) input.eventType = cfg.eventType;
  if (cfg.cron) input.cron = cfg.cron;
  if (cfg.tool) input.tool = cfg.tool;
  if (cfg.tools && Array.isArray(cfg.tools)) input.tools = cfg.tools;
  if (cfg.channel) input.channel = cfg.channel;
  if (cfg.recipients) input.recipients = cfg.recipients;
  if (cfg.url) input.url = cfg.url;
  if (cfg.method) input.method = cfg.method;

  const output: Record<string, unknown> = status === 'error'
    ? {
        _synthetic: true,
        error: {
          code: 'EXECUTION_TIMEOUT',
          message: `${nodeType} timed out after 30s`,
          retryable: true,
        },
      }
    : status === 'partial'
    ? {
        _synthetic: true,
        ok: true,
        warnings: ['Retried once before success'],
      }
    : {
        _synthetic: true,
        ok: true,
        note: 'Real output will appear here once the test-run engine wires in per-node payloads.',
      };

  return {
    status,
    durationMs: 120 + Math.floor(Math.random() * 2200),
    input,
    output,
  };
}

// ── Badge styles ────────────────────────────────────────────────────────

const BADGE_STYLES = {
  success: {
    icon: CheckCircle,
    bg: 'bg-emerald-500',
    ring: 'ring-emerald-500/30',
    text: 'text-white',
  },
  error: {
    icon: XCircle,
    bg: 'bg-red-500',
    ring: 'ring-red-500/30',
    text: 'text-white',
  },
  partial: {
    icon: AlertTriangle,
    bg: 'bg-amber-500',
    ring: 'ring-amber-500/30',
    text: 'text-white',
  },
};

// ── Format helpers ──────────────────────────────────────────────────────

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = (ms / 1000).toFixed(1);
  return `${s}s`;
}

function prettyJson(obj: Record<string, unknown> | null): string {
  if (obj === null) return '(no output)';
  return JSON.stringify(obj, null, 2);
}

function rawJson(obj: Record<string, unknown> | null): string {
  if (obj === null) return '(no output)';
  return JSON.stringify(obj);
}

// ── Component ───────────────────────────────────────────────────────────

export function NodeInspectBadge({ data, staggerIndex = 0 }: NodeInspectBadgeProps) {
  const [jsonView, setJsonView] = useState<'pretty' | 'raw'>('pretty');
  const style = BADGE_STYLES[data.status];
  const Icon = style.icon;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'absolute -top-2 -right-2 z-10 flex items-center justify-center',
            'w-5 h-5 rounded-full shadow-md cursor-pointer',
            'ring-2 ring-offset-1 ring-offset-background',
            style.bg, style.ring, style.text,
            'hover:scale-110 transition-transform',
            'animate-[badge-pop_200ms_ease-out_both]',
          )}
          style={{ animationDelay: `${staggerIndex * 80}ms` }}
          title={`Status: ${data.status} (${formatDuration(data.durationMs)})`}
          onClick={(e) => e.stopPropagation()}
        >
          <Icon className="h-3 w-3" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[380px] p-0"
        side="right"
        align="start"
        sideOffset={12}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
          <div className="flex items-center gap-2">
            <Icon className={cn('h-3.5 w-3.5', data.status === 'success' ? 'text-emerald-500' : data.status === 'error' ? 'text-red-500' : 'text-amber-500')} />
            <span className="text-xs font-semibold capitalize">{data.status}</span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span className="font-mono">{formatDuration(data.durationMs)}</span>
          </div>
        </div>

        {/* Synthetic preview banner: when input/output are placeholders
            (signalled by `_synthetic: true`), make that visible so
            reviewers never mistake them for real execution data (#F9).
            Guard against `output === null` (the truth-telling shape
            for a step that produced no output — see #197/#205). */}
        {(((data.input as { _synthetic?: boolean })._synthetic) ||
          (data.output !== null &&
            (data.output as { _synthetic?: boolean })._synthetic)) && (
          <div className="px-3 py-1.5 text-[10px] text-amber-700 dark:text-amber-300 bg-amber-500/10 border-b border-amber-500/20">
            Test data — synthetic preview. Real input/output will appear here once the runtime wires in per-node payloads.
          </div>
        )}

        {/* View toggle */}
        <div className="px-3 pt-2 pb-1">
          <Tabs value={jsonView} onValueChange={(v) => setJsonView(v as 'pretty' | 'raw')}>
            <TabsList className="h-7 w-full">
              <TabsTrigger value="pretty" className="text-[10px] h-5 flex-1">Pretty</TabsTrigger>
              <TabsTrigger value="raw" className="text-[10px] h-5 flex-1">Raw</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Data sections */}
        <ScrollArea className="max-h-[320px]">
          <div className="px-3 pb-3 space-y-2">
            {/* Input */}
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <Badge variant="outline" className="text-[8px] h-4 px-1.5 bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20">INPUT</Badge>
              </div>
              <pre className="text-[10px] leading-relaxed bg-muted/40 rounded-md p-2 overflow-x-auto font-mono text-foreground whitespace-pre-wrap break-all">
                {jsonView === 'pretty' ? prettyJson(data.input) : rawJson(data.input)}
              </pre>
            </div>

            {/* Output */}
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <Badge variant="outline" className={cn(
                  'text-[8px] h-4 px-1.5',
                  data.status === 'error'
                    ? 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20'
                    : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
                )}>OUTPUT</Badge>
              </div>
              <pre className="text-[10px] leading-relaxed bg-muted/40 rounded-md p-2 overflow-x-auto font-mono text-foreground whitespace-pre-wrap break-all">
                {jsonView === 'pretty' ? prettyJson(data.output) : rawJson(data.output)}
              </pre>
            </div>
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
